// EditorWizardBar — sticky red/cream step strip pinned to the top of an
// admin editor page. Walks basic users through the canonical creation
// flow (Mockup → Settings → Locations → Colours → Sizes → Save) while
// leaving every existing field underneath fully editable for power users.
//
// Behaviour:
// - Click a step pill → smooth-scroll to the matching `wiz-${step.id}` anchor.
// - Prev / Next walk the steps in order, scrolling on each move.
// - Steps marked `done` get a check; the current step is filled brick.
// - The bar is `position: sticky; top: 0` so it survives scrolling.
//
// Styling: brand brick (#A82A33) on cream (#FFFBF4) — distinct from the
// gold + ink palette used by the customer-facing surface so admins
// instantly see "this is the editor guide rail", not a customer header.
import React, { useEffect, useRef, useState } from 'react'

export type WizardStep = {
  id: string
  label: string
  /** Show a check mark next to the label when this is set. */
  done?: boolean
  /**
   * Optional click override. When omitted the bar scrolls to the
   * `wiz-${id}` element on the page (and adds a brief brick ring
   * highlight to make the destination obvious).
   */
  onClick?: () => void
}

export default function EditorWizardBar({
  title, steps, initialIndex = 0, onIndexChange, variant = 'sticky',
}: {
  /** Short label shown above the step strip — e.g. "Garment editor". */
  title: string
  steps: WizardStep[]
  initialIndex?: number
  onIndexChange?: (i: number) => void
  /**
   * `sticky` (default) — sticks to top of a `p-8` admin page (uses
   * `-mx-8` to bleed full-width). Use for admin editors.
   * `inline`           — plain band that sits in normal document flow,
   * no negative margins, no sticky. Use for the customer Designer
   * and DTF Builder which sit below a dark TopBar.
   */
  variant?: 'sticky' | 'inline'
}) {
  const [i, setI] = useState(initialIndex)
  const lastIdxRef = useRef(initialIndex)

  // Keep parent informed when admins move around (so we can persist
  // a "currently editing this step" hint if we ever want to).
  useEffect(() => {
    if (lastIdxRef.current === i) return
    lastIdxRef.current = i
    onIndexChange?.(i)
  }, [i, onIndexChange])

  function scrollToStep(idx: number) {
    const s = steps[idx]
    if (!s) return
    if (s.onClick) { s.onClick(); return }
    const el = document.getElementById(`wiz-${s.id}`)
    if (!el) return
    // Account for the sticky bar's own height by offsetting the scroll.
    const barEl = document.getElementById('editor-wizard-bar')
    const yOff = (barEl?.offsetHeight ?? 0) + 16
    const top = el.getBoundingClientRect().top + window.scrollY - yOff
    window.scrollTo({ top, behavior: 'smooth' })
    // Brief brick ring to telegraph "this is the section the wizard means".
    el.classList.add('ring-2', 'ring-brick', 'ring-offset-2', 'ring-offset-cream')
    window.setTimeout(() => {
      el.classList.remove('ring-2', 'ring-brick', 'ring-offset-2', 'ring-offset-cream')
    }, 1400)
  }

  function go(idx: number) {
    const clamped = Math.max(0, Math.min(steps.length - 1, idx))
    setI(clamped)
    scrollToStep(clamped)
  }

  return (
    <div
      id="editor-wizard-bar"
      className={[
        variant === 'sticky'
          ? 'sticky top-0 z-30 -mx-8 mb-6 px-8 py-3'
          : 'px-6 py-2.5 flex-shrink-0',
        'bg-cream border-b-2 border-brick shadow-sm',
      ].join(' ')}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-brick text-white text-xs font-bold">
            {i + 1}
          </span>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brick">{title}</div>
            <div className="text-sm font-semibold text-ink truncate">
              Step {i + 1} of {steps.length}: {steps[i]?.label ?? '—'}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto">
          {steps.map((s, idx) => {
            const active = idx === i
            const done   = !!s.done
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => go(idx)}
                className={[
                  'group relative shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-full',
                  'text-[11px] font-semibold uppercase tracking-wide transition-colors',
                  active
                    ? 'bg-brick text-white border border-brick'
                    : done
                      ? 'bg-brick-tint text-brick-dark border border-brick/40 hover:bg-brick/15'
                      : 'bg-white text-ink-3 border border-border hover:border-brick hover:text-brick',
                ].join(' ')}
                aria-current={active ? 'step' : undefined}>
                <span className={[
                  'inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold',
                  active
                    ? 'bg-white text-brick'
                    : done
                      ? 'bg-brick text-white'
                      : 'bg-border text-ink-3 group-hover:bg-brick group-hover:text-white',
                ].join(' ')}>
                  {done && !active ? (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ) : (
                    idx + 1
                  )}
                </span>
                <span>{s.label}</span>
              </button>
            )
          })}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => go(i - 1)}
            disabled={i === 0}
            className="px-3 py-1.5 rounded border border-brick/40 text-brick text-xs font-semibold uppercase tracking-wide hover:bg-brick-tint disabled:opacity-30 disabled:cursor-not-allowed">
            ← Prev
          </button>
          <button
            type="button"
            onClick={() => go(i + 1)}
            disabled={i === steps.length - 1}
            className="px-3 py-1.5 rounded bg-brick text-white text-xs font-semibold uppercase tracking-wide hover:bg-brick-dark disabled:opacity-40 disabled:cursor-not-allowed">
            Next →
          </button>
        </div>
      </div>
    </div>
  )
}
